/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { getProjectionData } from '../../../../../api/trust';
import { Loader, Table } from '../../../../../common/components';
import { trustDyanamicItemWisePayementsSections } from '../../../../../utils/constant';
import { formatPrice } from '../../../../../utils/formatters';
import { apiErrorHandler } from '../../../../../utils/handlers';
import ItemEmptyTile from '../../../../reports/item-empty-tile/item-empty-tile';
import TrustLegend from '../../trustLegend/TrustLegend';

export default function PayableTable({
  agreementId,
  paidTableRef,
  equityAndCancelledTableRef,
  setDisabled,
}) {
  const [loading, setLoading] = useState(false);
  const [totaldata, settotalData] = useState({});

  useEffect(() => {
    if (agreementId) fetchGLTemplateList();
  }, [agreementId]);

  const handlePaidFactSheetData = (err, data) => {
    if (paidTableRef.current?.getProjectionData) paidTableRef.current.getProjectionData(err, data);
    if (equityAndCancelledTableRef?.current?.getProjectionData)
      equityAndCancelledTableRef.current.getProjectionData(err, data);
  };

  const fetchGLTemplateList = async () => {
    setLoading(true);
    handlePaidFactSheetData(null, { loading: true });
    try {
      const getProjection = await getProjectionData({
        id: agreementId,
        contractType: 1,
      });

      handlePaidFactSheetData(null, {
        loading: false,
        data: { getProjection },
      });
      setLoading(false);
      setDisabled(false);
      settotalData(getProjection?.data?.result);
    } catch (err) {
      setLoading(false);
      setDisabled(false);
      apiErrorHandler(err);
      handlePaidFactSheetData(err, null);
    }
  };

  const spaceFiller = (key, length, padding, fillWith) => {
    return Array(length)
      .fill(0)
      .slice(padding)
      .map((_val, index) => <td key={`${key}-${index.toString()}`}>{fillWith || ''}</td>);
  };

  return (
    <Card className="table-payable">
      <CardHeader>
        <TrustLegend title="Payable (Monthly projections)" />
      </CardHeader>
      <CardBody>
        {!loading &&
        totaldata?.monthlyProjections &&
        Object.entries(totaldata?.monthlyProjections).length > 0 ? (
          <Table
            headers={totaldata?.monthlyProjections.headers}
            bordered
            responsive
            className="trustaccounts-table"
          >
            <tbody>
              {/* Finance Charges Mapping */}
              <tr>
                <td>{totaldata?.monthlyProjections?.financeCharges[0] || 'Finance Charges'}</td>
                <td />
                {totaldata?.monthlyProjections?.financeCharges.length > 0
                  ? totaldata?.monthlyProjections?.financeCharges
                      .slice(2)
                      .map((FCItem, index) => (
                        <td key={`fc-${index.toString()}`}>{formatPrice(FCItem || '') || '-'}</td>
                      ))
                  : totaldata?.monthlyProjections?.headers
                      .slice(2)
                      .map((_FCItem, index) => <td key={`fc-${index.toString()}`}>-</td>)}
              </tr>
              {/* Tax Mapping */}
              <tr>
                <td>{totaldata?.monthlyProjections?.tax[0] || 'Total Sales Tax'}</td>
                <td />
                {totaldata?.monthlyProjections?.tax.slice(2).map((taxItem, index) => (
                  <td key={`t-${index.toString()}`}>{formatPrice(taxItem || '') || '-'}</td>
                ))}
                {spaceFiller(
                  'tax',
                  totaldata?.monthlyProjections?.headers?.length,
                  totaldata?.monthlyProjections?.tax?.length || 2,
                  '-',
                )}
              </tr>
              {/* Sections Mapping */}
              {trustDyanamicItemWisePayementsSections.map((section) => {
                return (
                  totaldata?.monthlyProjections?.[section?.keyTobeMapped].length > 0 && (
                    <>
                      <tr className="row-spacer">
                        <td className="font-heavy">{section?.title}</td>
                        {spaceFiller('properties', totaldata.monthlyProjections.headers.length, 1)}
                      </tr>
                      {totaldata?.monthlyProjections?.[section?.keyTobeMapped].map(
                        (item, index) => (
                          <tr key={`${section.keyTobeMapped}-p-${index.toString()}`}>
                            <td>
                              {item[0].split('\n').length > 1 ? (
                                <>
                                  {item[0].split('\n')[0]}
                                  {item[0].split('\n')[1] ? (
                                    <>
                                      <br />{' '}
                                      <small className="isTrustableText">
                                        {item[0].split('\n')[1]}
                                      </small>{' '}
                                    </>
                                  ) : (
                                    ''
                                  )}
                                </>
                              ) : (
                                item[0]
                              )}
                            </td>
                            {section.keyTobeMapped === 'adjustments' ||
                            section.keyTobeMapped === 'discounts' ? (
                              <td />
                            ) : (
                              <td className={`${item[1] ? 'isTrustableText' : ''}`}>
                                {item[1] ? 'Yes' : 'No'}
                              </td>
                            )}
                            <td>{formatPrice(item[2] || '0')}</td>
                            <td>{formatPrice(item[3] || '0')}</td>
                            {section.keyTobeMapped === 'adjustments' ||
                            section.keyTobeMapped === 'discounts'
                              ? spaceFiller(
                                  'adjustments',
                                  totaldata.monthlyProjections.headers.length,
                                  4,
                                )
                              : item
                                  .slice(4)
                                  .map((subItem, subIndex) => (
                                    <td key={`${section.keyTobeMapped}-si-${subIndex.toString()}`}>
                                      {formatPrice(subItem || '') || '-'}
                                    </td>
                                  ))}
                          </tr>
                        ),
                      )}
                    </>
                  )
                );
              })}
              {/* Total Contract Mapping */}
              <tr className="row-spacer">
                <td>Total Contract</td>
                <td />
                <td>{formatPrice(totaldata.totalContract || 0)}</td>
                {spaceFiller('totalContract', totaldata.monthlyProjections.headers.length, 3)}
              </tr>
              {/* Equal Monthly Payments Mapping */}
              {totaldata.monthlyProjections?.equalMonthlyPayments && (
                <tr>
                  <td>{totaldata.monthlyProjections?.equalMonthlyPayments[0]}</td>
                  <td />
                  <td />
                  <td />
                  {totaldata.monthlyProjections?.equalMonthlyPayments
                    .slice(3)
                    .map((EQItem, EQIndex) => {
                      return <td key={`eq-${EQIndex.toString()}`}>{formatPrice(EQItem) || ''}</td>;
                    })}
                </tr>
              )}
            </tbody>
          </Table>
        ) : (
          <ItemEmptyTile />
        )}
      </CardBody>
      {loading && <Loader />}
    </Card>
  );
}
